// /join — claim your invitation.
//
// Auto-mounted at /join by the pages routing convention. Sign in with the address
// an organizer allowlisted and the invitation is already waiting: confirm it's
// you, hit Join. There is no code to type, keep, or lose.
//
// The share code this page used to take is gone (#34). It was a bearer token with
// no binding to a person — whoever held it could join as any household, and the
// anonymous lookup that resolved it leaked the family roster to anyone who
// guessed. Membership binds to a verified email address instead.
//
// The household chooser this page used to show is gone too (#50). Proving which
// address you own never proved which household is yours, so choosing was an
// invitation to walk into someone else's intake. The invitation now names the
// household; this page only confirms it. If nobody has assigned one, the honest
// answer is "an organizer still has to do that", not a list of six families.

import { useCallback, useEffect, useState, type CSSProperties, type JSX } from 'react'
import { navigate } from '../App'
import { FUNCTIONS_URL, SUPABASE_PUBLISHABLE_KEY, supabase } from '../lib/supabaseClient'
import { AuthProvider, RequireAuth, getAccessToken, useAuth } from '../lib/auth'

interface Household {
  id: string
  label: string
}

interface Invitation {
  trip_id: string
  trip_name: string
  /** Null until an organizer assigns one — see the `unassigned` state below. */
  household: Household | null
}

const styles: Record<string, CSSProperties> = {
  shell: {
    maxWidth: '34rem',
    margin: '0 auto',
    padding: '2.5rem 1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  household: {
    padding: '1rem',
    borderRadius: '0.5rem',
    border: '2px solid var(--accent)',
    fontSize: '1.125rem',
    fontWeight: 600,
  },
  button: {
    width: '100%',
    minHeight: '3rem',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    fontWeight: 600,
    borderRadius: '0.5rem',
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    cursor: 'pointer',
  },
  muted: { color: 'var(--muted)' },
  error: { color: '#b3261e', fontWeight: 600 },
}

type State =
  | { kind: 'loading' }
  /** Already a member — nothing to claim, just a way onward. */
  | { kind: 'joined'; invitation: Invitation; household: Household | null }
  /** Invited AND assigned a household — the only state with a Join button. */
  | { kind: 'invited'; invitation: Invitation; household: Household }
  /** Allowlisted, but no organizer has said which household. Cannot join yet. */
  | { kind: 'unassigned'; tripName: string }
  | { kind: 'not-invited' }
  | { kind: 'error'; message: string }

function JoinInner(): JSX.Element {
  const { user } = useAuth()
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true

    void (async () => {
      const token = await getAccessToken()
      if (!active) return
      if (!token) {
        window.location.replace(`/login?next=${encodeURIComponent('/join')}`)
        return
      }

      try {
        const res = await fetch(`${FUNCTIONS_URL}/join`, {
          headers: { Authorization: `Bearer ${token}`, apikey: SUPABASE_PUBLISHABLE_KEY },
        })
        if (!active) return

        if (res.status === 403) {
          setState({ kind: 'not-invited' })
          return
        }
        if (!res.ok) {
          setState({ kind: 'error', message: 'Could not load your invitation. Try again shortly.' })
          return
        }

        const invitation = (await res.json()) as Invitation

        // Someone already on the trip does not need to join it again. The POST is
        // idempotent so pressing it was harmless, but offering it reads as "you are not
        // in yet" to a member who simply navigated here.
        const { data: existing } = await supabase
          .from('membership')
          .select('trip_id')
          .eq('trip_id', invitation.trip_id)
          .eq('user_id', user?.id ?? '')
          .maybeSingle()
        if (!active) return
        if (existing) {
          setState({ kind: 'joined', invitation, household: invitation.household })
          return
        }

        setState(
          invitation.household
            ? { kind: 'invited', invitation, household: invitation.household }
            : { kind: 'unassigned', tripName: invitation.trip_name },
        )
      } catch {
        if (!active) return
        setState({ kind: 'error', message: 'Could not reach the server. Check your signal.' })
      }
    })()

    return () => {
      active = false
    }
  }, [user?.id])

  const join = useCallback(async () => {
    if (state.kind !== 'invited') return
    setJoining(true)
    setError(null)

    const token = await getAccessToken()
    if (!token) {
      window.location.replace(`/login?next=${encodeURIComponent('/join')}`)
      return
    }

    try {
      const res = await fetch(`${FUNCTIONS_URL}/join`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_PUBLISHABLE_KEY,
          'Content-Type': 'application/json',
        },
        // No household_id: the server takes it from the invitation and ignores
        // anything we might send. Sending one would only imply we had a say.
        body: JSON.stringify({ trip_id: state.invitation.trip_id }),
      })

      if (res.status === 401) {
        window.location.replace(`/login?next=${encodeURIComponent('/join')}`)
        return
      }
      if (!res.ok) {
        setError('Could not join the trip. Try again, or tell an organizer.')
        setJoining(false)
        return
      }
      navigate(`/intake?trip=${encodeURIComponent(state.invitation.trip_id)}`)
    } catch {
      setError('Could not reach the server. Check your signal and try again.')
      setJoining(false)
    }
  }, [state])

  if (state.kind === 'loading') {
    return (
      <main style={styles.shell}>
        <p style={styles.muted}>Looking for your invitation&hellip;</p>
      </main>
    )
  }

  if (state.kind === 'not-invited') {
    return (
      <main style={styles.shell}>
        <h1>You&rsquo;re not on the list yet</h1>
        <p>
          We don&rsquo;t have an invitation for <strong>{user?.email ?? 'this address'}</strong>.
        </p>
        <p style={styles.muted}>
          Ask an organizer to add it — it takes them seconds. If the family might know you by a
          different email address, sign out and try that one.
        </p>
        <p>
          <a href="/">Back to the trip</a>
        </p>
      </main>
    )
  }

  if (state.kind === 'error') {
    return (
      <main style={styles.shell}>
        <h1>Something went wrong</h1>
        <p style={styles.error} role="alert">
          {state.message}
        </p>
      </main>
    )
  }

  if (state.kind === 'joined') {
    return (
      <main style={styles.shell}>
        <h1>{state.invitation.trip_name}</h1>
        <p>
          You&rsquo;re already on this trip
          {state.household ? (
            <>
              , in <strong>{state.household.label}</strong>
            </>
          ) : null}
          .
        </p>
        <p style={styles.muted}>
          Nothing to do here. If that household is wrong, tell an organizer &mdash; they can
          move you.
        </p>
        <p>
          <a href={`/intake?trip=${encodeURIComponent(state.invitation.trip_id)}`}>
            Your household&rsquo;s answers
          </a>
        </p>
      </main>
    )
  }

  // Allowlisted but unassigned. Showing the six households here and letting them
  // choose is precisely the bug (#50) — so say what's actually true instead.
  if (state.kind === 'unassigned') {
    return (
      <main style={styles.shell}>
        <h1>{state.tripName}</h1>
        <p>
          You&rsquo;re on the list as <strong>{user?.email}</strong>, but an organizer
          hasn&rsquo;t said which household you&rsquo;re in yet.
        </p>
        <p style={styles.muted}>
          Ask Susan or Jeff to assign you — it&rsquo;s one dropdown on the trip setup screen.
          Come back to this page afterwards and you&rsquo;ll be able to join.
        </p>
        <p>
          <a href="/">Back to the trip</a>
        </p>
      </main>
    )
  }

  const { invitation, household } = state

  return (
    <main style={styles.shell}>
      <h1>{invitation.trip_name}</h1>
      <p style={styles.muted}>
        You&rsquo;re invited as <strong>{user?.email}</strong>, in this household:
      </p>

      <p style={styles.household}>{household.label}</p>

      <p style={styles.muted}>
        Not right? Don&rsquo;t join — tell an organizer instead. They can move you, and joining
        the wrong household would put your dates and requirements in another family&rsquo;s
        intake.
      </p>

      {error ? (
        <p style={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      <button type="button" style={styles.button} onClick={() => void join()} disabled={joining}>
        {joining ? 'Joining…' : 'Join trip'}
      </button>
    </main>
  )
}

export default function JoinPage(): JSX.Element {
  // Self-wrapped AuthProvider (nest-safe) so the page works before the root
  // provider in main.tsx mounts.
  return (
    <AuthProvider>
      <RequireAuth>
        <JoinInner />
      </RequireAuth>
    </AuthProvider>
  )
}
